import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false,
  });

  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setValidating(false);
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    setPasswordValidation({
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  }, [password]);

  const isPasswordStrong = Object.values(passwordValidation).every(Boolean);

  const validateToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/validate-reset-token?token=${token}`);
      setTokenValid(true);
      setUserInfo(response.data);
    } catch (err) {
      setTokenValid(false);
      setError(err.response?.data?.detail || 'Invalid or expired reset link');
    } finally {
      setValidating(false);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!isPasswordStrong) {
      setError('Password does not meet requirements');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Invalid Reset Link</h1>
          <p className="text-slate-500 dark:text-slate-400 mb-6">No reset token provided. Please request a new password reset from your administrator.</p>
          <Link to="/login" className="btn-primary inline-block">Back to Login</Link>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Link Expired or Invalid</h1>
          <p className="text-slate-500 dark:text-slate-400 mb-6">{error || 'This password reset link is no longer valid. Please contact your administrator for a new reset link.'}</p>
          <Link to="/login" className="btn-primary inline-block">Back to Login</Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Password Reset Successfully!</h1>
          <p className="text-slate-500 dark:text-slate-400 mb-6">Your password has been changed. You can now login with your new password.</p>
          <Link to="/login" className="btn-primary inline-block">Go to Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 md:px-16 lg:px-24 bg-white dark:bg-slate-800">
        <div className="max-w-md w-full mx-auto">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-8">
              <img 
                src="/logo-red.png" 
                alt="intalk.io" 
                className="h-24 w-auto object-contain"
              />
            </div>
            <h1 className="text-3xl font-semibold text-slate-900 dark:text-white tracking-tight">Reset Password</h1>
            <p className="mt-2 text-slate-500 dark:text-slate-400 font-body">
              {userInfo?.name ? `Hi ${userInfo.name}, ` : ''}Create a new secure password for your account
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 border border-red-200 bg-red-50 rounded-lg" role="alert">
              <p className="text-sm text-red-700 font-body">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="form-label">Email</label>
              <input
                type="email"
                value={userInfo?.email || ''}
                disabled
                className="input-field bg-slate-50 dark:bg-slate-900"
              />
            </div>

            <div>
              <label className="form-label">New Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
                placeholder="Enter new password"
                data-testid="reset-password-input"
              />
              {password && (
                <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                  <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-widest mb-2">Password Requirements</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <p className={passwordValidation.minLength ? 'text-green-600' : 'text-slate-400'}>✓ 8+ characters</p>
                    <p className={passwordValidation.hasUppercase ? 'text-green-600' : 'text-slate-400'}>✓ Uppercase letter</p>
                    <p className={passwordValidation.hasLowercase ? 'text-green-600' : 'text-slate-400'}>✓ Lowercase letter</p>
                    <p className={passwordValidation.hasNumber ? 'text-green-600' : 'text-slate-400'}>✓ Number</p>
                    <p className={passwordValidation.hasSpecial ? 'text-green-600' : 'text-slate-400'}>✓ Special character</p>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="form-label">Confirm New Password</label>
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-field"
                placeholder="Confirm new password"
                data-testid="reset-confirm-password-input"
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="mt-1 text-xs text-red-500">Passwords do not match</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !isPasswordStrong || password !== confirmPassword}
              className="w-full btn-primary py-3 disabled:opacity-50"
              data-testid="reset-password-submit"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-slate-500 dark:text-slate-400 font-body">
            Remember your password?{' '}
            <Link to="/login" className="font-medium text-slate-900 dark:text-white hover:text-blue-600 transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700">
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center max-w-lg">
            <div className="w-20 h-20 mx-auto mb-8 rounded-2xl bg-white dark:bg-slate-800/20 backdrop-blur flex items-center justify-center">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Secure Password Reset</h2>
            <p className="text-blue-100 font-body text-lg leading-relaxed">
              Create a strong password that includes uppercase and lowercase letters, numbers, and special characters.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
